import type { Agreement, LinkState } from './rules.ts';

// What a manager or an admin may do to one profile (plan 5B Chunk 6), as pure functions:
// what a pardon clears, what each named edit writes, what typing a handle must match to
// purge, and how a tag on the review page is coloured. The server reads and writes.

/** A pardon and an edit each give a reason, kept on the audit trail. */
export const MAX_REASON = 1000;

/** `members_profiles.ban_reason` holds this much. */
export const MAX_BAN_REASON = 1000;

/**
 * R2's last line: only a pardon clears a judgment. It clears the flags, their reasons, the
 * hostile orgs found and the ban reason, and nothing else: `org_history` only grows, so a
 * past hostile org counts against them for ever (Assumption 1), and the agreement stays.
 */
export const PARDON_PATCH = {
  permanent_threat: false,
  is_threat: false,
  threat_reasons: [] as string[],
  hostile_orgs: [] as string[],
  ban_reason: '',
  evasive: false,
};

/** What a pardon would clear: whether anything is held at all. */
export function isHeld(facts: { isThreat: boolean; permanentThreat: boolean; evasive: boolean; threatReasons: readonly string[]; hostileOrgs: readonly string[]; banReason: string }): boolean {
  return facts.isThreat || facts.permanentThreat || facts.evasive || facts.threatReasons.length > 0 || facts.hostileOrgs.length > 0 || facts.banReason !== '';
}

// --- The named edits (admin) --------------------------------------------------------------

/** The only things an admin may change by hand. The console's "edit any field" is not carried over. */
export const EDITS = ['link_state', 'is_threat', 'permanent_threat', 'agreement', 'ban_reason'] as const;
export type EditName = (typeof EDITS)[number];

export const EDIT_LABELS: Readonly<Record<EditName, string>> = {
  link_state: 'The link',
  is_threat: 'Flagged as a threat',
  permanent_threat: 'Banned',
  agreement: 'The anti-piracy agreement',
  ban_reason: 'The ban reason',
};

export type EditRequest =
  | { edit: 'link_state'; value: LinkState }
  | { edit: 'is_threat'; value: boolean }
  | { edit: 'permanent_threat'; value: boolean }
  | { edit: 'agreement'; value: Agreement }
  | { edit: 'ban_reason'; value: string };

/** What an edit reads of a profile. */
export interface EditFacts {
  discordId: string;
  linkState: LinkState;
  linkedAt: Date | null;
  isThreat: boolean;
  permanentThreat: boolean;
  agreement: Agreement;
  banReason: string;
}

export interface EditPatch {
  link_state?: LinkState;
  linked_at?: string;
  is_threat?: boolean;
  permanent_threat?: boolean;
  agreement?: Agreement;
  ban_reason?: string;
}

export type EditOutcome = { ok: true; patch: EditPatch } | { ok: false; message: string };

const ALREADY: EditOutcome = { ok: false, message: 'It is already that way, so nothing was changed' };

/**
 * One edit's patch. A link can't be set on a profile with no Discord account to link, and
 * `linked_at` is set only for a link someone vouched for: `pre_migration` is unproven by
 * definition. Banning also flags, as an import does and as `/join` does; unbanning leaves the
 * flag, which a pardon clears with its reasons.
 */
export function editPatch(facts: EditFacts, request: EditRequest, now: Date): EditOutcome {
  switch (request.edit) {
    case 'link_state': {
      if (request.value === facts.linkState) return ALREADY;
      if (request.value !== '' && facts.discordId === '') return { ok: false, message: 'There is no Discord account on this profile to link' };
      if (request.value === 'verified') return { ok: true, patch: { link_state: 'verified', ...(facts.linkedAt === null ? { linked_at: now.toISOString().replace('T', ' ') } : {}) } };
      return { ok: true, patch: { link_state: request.value, linked_at: '' } };
    }
    case 'is_threat':
      return request.value === facts.isThreat ? ALREADY : { ok: true, patch: { is_threat: request.value } };
    case 'permanent_threat':
      if (request.value === facts.permanentThreat) return ALREADY;
      return { ok: true, patch: request.value ? { permanent_threat: true, is_threat: true } : { permanent_threat: false } };
    case 'agreement':
      return request.value === facts.agreement ? ALREADY : { ok: true, patch: { agreement: request.value } };
    case 'ban_reason': {
      const value = request.value.trim();
      if (value.length > MAX_BAN_REASON) return { ok: false, message: `A ban reason is at most ${String(MAX_BAN_REASON)} characters` };
      return value === facts.banReason ? ALREADY : { ok: true, patch: { ban_reason: value } };
    }
  }
}

// --- Purging ----------------------------------------------------------------------------

/**
 * What must be typed to purge: the handle, or the profile's id if it has none. The browser
 * asks for it and the server checks it again, ignoring case as RSI does.
 */
export function purgeConfirmation(profile: { id: string; handle: string }): string {
  return profile.handle === '' ? profile.id : profile.handle;
}

export function confirmsPurge(typed: string, profile: { id: string; handle: string }): boolean {
  return typed.trim().toLowerCase() === purgeConfirmation(profile).toLowerCase();
}

// --- The review queue's colours -----------------------------------------------------------

/** `hostile` is a symbol the settings call hostile, or one found on this profile. `pirate` is an org with a threat activity. */
export const ORG_TONES = ['hostile', 'pirate', 'neutral'] as const;
export type OrgTone = (typeof ORG_TONES)[number];

export interface ToneRules {
  /** The `hostileOrgs` setting. */
  hostileOrgs: readonly string[];
  /** The `threatAffiliations` setting. */
  threatAffiliations: readonly string[];
}

/**
 * How review.py colours a tag: red for an org the settings call hostile or the profile's own
 * scan found, orange for one whose activities hold a threat, plain otherwise. Case is ignored.
 * `activities` are what `members_orgs` (and, for a current org, the profile) say the org does.
 */
export function orgTone(symbol: string, found: readonly string[], activities: readonly string[], rules: ToneRules): OrgTone {
  const key = symbol.toUpperCase();
  if (rules.hostileOrgs.some((hostile) => hostile.toUpperCase() === key) || found.some((hostile) => hostile.toUpperCase() === key)) return 'hostile';
  const threats = new Set(rules.threatAffiliations.map((activity) => activity.toLowerCase()));
  return activities.some((activity) => threats.has(activity.toLowerCase())) ? 'pirate' : 'neutral';
}
